'use client'

import { useRouter } from 'next/navigation'
import { Suspense, useCallback, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-nextjs/components'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import SignInForm from '@/components/WorkOsForm/components/SignInForm'
import SignUpForm from '@/components/WorkOsForm/components/SignUpForm'
import { refreshCurrentUser } from '@/hooks/useCurrentUser'
import { resolveSafeRedirect } from '@/utilities/safeRedirect'

type AuthMode = 'signup' | 'signin'

export type EventAuthModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  redirectTo: string
  defaultMode?: AuthMode
  onAuthenticated?: () => void | Promise<void>
}

export const EventAuthModal = ({
  open,
  onOpenChange,
  redirectTo,
  defaultMode = 'signup',
  onAuthenticated,
}: EventAuthModalProps) => {
  const router = useRouter()
  const { getAuth } = useAuth()
  const [mode, setMode] = useState<AuthMode>(defaultMode)
  const safeRedirectTo = resolveSafeRedirect(redirectTo) ?? '/dashboard'

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setMode(defaultMode)
    }
    onOpenChange(nextOpen)
  }

  const handleAuthenticated = useCallback(async () => {
    await getAuth()
    await refreshCurrentUser()
    router.refresh()
    await onAuthenticated?.()
  }, [getAuth, onAuthenticated, router])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl gap-0 overflow-y-auto p-0 sm:rounded-2xl">
        <DialogTitle className="sr-only">
          {mode === 'signup' ? 'Create your account' : 'Sign in to your account'}
        </DialogTitle>
        <Suspense fallback={null}>
          {mode === 'signup' ? (
            <SignUpForm
              variant="modal"
              redirectTo={safeRedirectTo}
              onSwitchToSignIn={() => setMode('signin')}
              onAuthenticated={handleAuthenticated}
            />
          ) : (
            <SignInForm
              variant="modal"
              redirectTo={safeRedirectTo}
              onSwitchToSignUp={() => setMode('signup')}
              onAuthenticated={handleAuthenticated}
            />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  )
}
